import { useRef, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { useI18next, useTranslation } from 'gatsby-plugin-react-i18next';
import { ArrayParam, NumberParam, useQueryParam } from 'use-query-params';
import { useQuery } from 'react-query';

import generateWatchProjectImage from '../../utils/generateWatchProjectImage';
import { getMagazines, SortingTypes } from '../../utils/api';
import { SearchGridResultsMapper } from '../SearchGrid';
import { PaginationProvider } from '../../utils/ctx/PaginationContext';
import { dateFormatOptions } from '../../utils/i18n/format';

import ArrowDown from '../../images/icons/arrow.svg';
import Pagination from '../Pagination';
import { I18NextContext } from 'gatsby-plugin-react-i18next/dist/types';

const SEARCH_RESULTS_PER_PAGE = 9;

const imagesQuery = graphql`
query {
  bigImage: file(base: {eq: "Omega-2.jpg"}) {
    publicURL
  }
}
`

const categories = [
  'editorial',
  'investments',
  'watchesAndTechnic',
  'buyingGuide'
]

const preMapMagazines = (data, bigImage, t) =>
  data['hydra:member'].map(((res, index) => {
    if (index === 0) {
      res.topImage = bigImage.publicURL;
    }
    res.category = res.category ? t(`category.${res.category}`) : '';
    return res;
  }))


const magazineMappingFunction = (localeContext: I18NextContext, result, index) => ({
  title: index === 0
    ? <div>
      <span className="font-cirka text-lg text-primary mb-4" >{result.category}</span>
      <h1 className="font-cirka text-4xl lg:text-6xl text-left text-primary mb-8 line-clamp-2" >{result.title}</h1>
      <time className="hidden md:inline text-gray-400 text-lg" dateTime={result.createdAt}>{new Date(result.createdAt).toLocaleDateString(localeContext.language, dateFormatOptions)}</time>
      <div className="hidden md:flex my-8 font-roboto text-lg text-black">
        {`${result.topText.replace(/<\/?[^>]+(>|$)/g, "").split(' ', 35).join(" ")}...`}
      </div>
    </div>
    : <div>
      <span className="font-cirka text-lg text-primary">{result.category}</span>
      <h3 className="font-cirka text-3xl text-primary my-4 flex h-auto line-clamp-3">{result.title}</h3>
    </div>,
  image: index === 0 ? result.topImage : generateWatchProjectImage(result.topImage, result.title),
  link: `/${result.type}/${result.slug.replace(/^\/|\/$/g, '')}`,
  className: `relative flex flex-col ${index === 0 ? 'grid-primary-item' : 'grid-secondary-item'}`,
  border: index === 0,
  imageClass: 'w-full object-cover',
  imageWrapperClass: index === 0 ? 'h-1/4 lg:h-1/2' : '',
  textWrapperClass: 'px-0 pb-0 md:p-4 flex-grow py-4 !px-0',
  hasAnimatedImage: false,
  type: 'buy',
  linkStyle: 'mb-0',
  buttonClass: 'absolute right-0 bottom-0 -translate-x-1/2 -translate-y-1/2'
})

const MagazineGrid = () => {
  const { t } = useTranslation();
  const { language } = useI18next()
  const [page = 1, setPage] = useQueryParam('page', NumberParam);
  const [filterCategory = [], setCategory] = useQueryParam('category', ArrayParam); // <----
  const paginationTopScrollRef = useRef(null);
  const [sorting, setSorting] = useState<SortingTypes>('createdAt_desc');
  const { bigImage } = useStaticQuery(imagesQuery);

  const handleCategoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!filterCategory) {
      return;
    }

    filterCategory.includes(e.target.id) ? setCategory(filterCategory.filter(f => f !== e.target.id)) : setCategory([...filterCategory, e.target.id]);
  }

  const handleOrderChange = (e: React.ChangeEvent<HTMLSelectElement>) => setSorting(e.target.value as SortingTypes);

  const { data, isLoading } = useQuery(`magazines/${page}/${filterCategory?.join(',')}/${sorting}}`,
    () => getMagazines({
      limit: SEARCH_RESULTS_PER_PAGE,
      filters: {
        category: filterCategory as string[],
        language: [language]
      },
      page: page as number,
      sorting
    }));

  if (data) {
    data['hydra:member'] = preMapMagazines(data, bigImage, t);
  }

  return (
    <section className="pb-12 px-4 lg:px-8 md:pl-12 max-w-screen-2xl mx-auto">
      <div className="pt-12 lg:pt-24">
        <h3 className="font-cirka font-light text-xl text-center">{t('magazineGrid.explore')}</h3>
        <hr className="mt-6 mb-4" />
        <details className="flex w-full lg:px-5" ref={paginationTopScrollRef}>
          <summary className="text-sm lg:text-xl text-primary flex items-center justify-between w-full">
            <span className="flex items-center">
              <ArrowDown className="transition-all duration-100 fill-primary h-3 mr-4" />
              {t('magazineHero.filter')}
            </span>
            <div className="text-sm lg:text-lg">
              <span className="hidden lg:block mr-4 text-black">{t('general.sort')}</span>
              <select className="text-primary font-light bg-transparent" onChange={handleOrderChange}>
                <option value="null">{t('general.sort.relevance')}</option>
                <option value="createdAt_asc">{t('general.sort.oldest')}</option>
                <option value="createdAt_desc">{t('general.sort.newest')}</option>
              </select>
            </div>
          </summary>
          <div className="my-4 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            <div className="p-4"><b>{t('general.category')}</b>
              <div className="flex flex-col h-auto overflow-y-hidden flex-wrap">
                {categories.map(category => (
                  <div key={category} className="my-2 flex">
                    <input type="checkbox" id={category} onChange={handleCategoryChange} checked={filterCategory?.includes(category)} />
                    <label htmlFor={category}>{t(`category.${category}`)}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </details>
      </div>
      <SearchGridResultsMapper
        query="query"
        data={data}
        isLoading={isLoading}
        resultMappingFunction={magazineMappingFunction}
        gridClasses='grid xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-6 p-5'
      />
      <div className="mx-20">
        <PaginationProvider currentPage={page || 1} setPage={setPage} scrollRef={paginationTopScrollRef}>
          <Pagination maxPages={data && Math.ceil(data['hydra:totalItems'] / SEARCH_RESULTS_PER_PAGE)} />
        </PaginationProvider>
      </div>
    </section>
  );
};

export default MagazineGrid;
